const SERVICE_WORKER_URL = `${process.env.BASE_URL}sw.js`
let installPromptEvent = null
const installStateListeners = new Set()
const notificationStateListeners = new Set()

function isStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
}

function getServiceWorkerState() {
  if (!('serviceWorker' in navigator)) return 'unsupported'
  if (navigator.serviceWorker.controller) return 'active'
  return 'available'
}

function getInstallState() {
  return {
    canInstall: Boolean(installPromptEvent),
    isInstalled: isStandaloneMode(),
    isInstallSupported: 'BeforeInstallPromptEvent' in window || Boolean(installPromptEvent),
    serviceWorkerState: getServiceWorkerState()
  }
}

function notifyInstallStateListeners() {
  const state = getInstallState()
  installStateListeners.forEach(listener => listener(state))
}

function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported'
  return Notification.permission
}

function notifyNotificationStateListeners() {
  const state = getNotificationPermission()
  notificationStateListeners.forEach(listener => listener(state))
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault()
    installPromptEvent = event
    notifyInstallStateListeners()
  })

  window.addEventListener('appinstalled', () => {
    installPromptEvent = null
    notifyInstallStateListeners()
  })
}

function clearAppCaches() {
  if (!('caches' in window)) return Promise.resolve()
  return caches.keys()
    .then(keys => Promise.all(keys
      .filter(key => key.startsWith('optometry-academy-admin'))
      .map(key => caches.delete(key))
    ))
}

function reloadWhenControllerChanges(timeout = 1200) {
  return new Promise(resolve => {
    let resolved = false
    const finish = () => {
      if (resolved) return
      resolved = true
      resolve()
    }

    navigator.serviceWorker.addEventListener('controllerchange', finish, { once: true })
    window.setTimeout(finish, timeout)
  })
}

export function registerServiceWorker() {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return Promise.resolve(null)
  }

  return window.addEventListener('load', () => {
    navigator.serviceWorker.register(SERVICE_WORKER_URL)
      .then(registration => {
        registration.update()
        notifyInstallStateListeners()
      })
      .catch(() => {})
  })
}

export function subscribeToPwaInstallState(listener) {
  listener(getInstallState())
  installStateListeners.add(listener)

  return () => {
    installStateListeners.delete(listener)
  }
}

export function subscribeToNotificationPermission(listener) {
  listener(getNotificationPermission())
  notificationStateListeners.add(listener)

  return () => {
    notificationStateListeners.delete(listener)
  }
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('Этот браузер не поддерживает системные уведомления.')
  }

  const permission = await Notification.requestPermission()
  notifyNotificationStateListeners()
  return permission
}

export async function showSystemNotification(payload = {}) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return false
  }

  const notificationPayload = {
    title: payload.title || 'Optometry Academy',
    body: payload.body || payload.message || '',
    url: payload.url || `${process.env.BASE_URL}`,
    tag: payload.tag || `admin-notification-${Date.now()}`
  }

  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration()
    if (registration && registration.active) {
      registration.active.postMessage({
        type: 'SHOW_NOTIFICATION',
        payload: notificationPayload
      })
      return true
    }
  }

  const notification = new Notification(notificationPayload.title, {
    body: notificationPayload.body,
    icon: `${process.env.BASE_URL}img/pwa/icon-192.png`,
    tag: notificationPayload.tag
  })
  notification.onclick = () => {
    window.focus()
    if (notificationPayload.url) window.location.href = notificationPayload.url
    notification.close()
  }
  return true
}

export async function installPwa() {
  if (isStandaloneMode()) {
    return { outcome: 'installed' }
  }

  if (!installPromptEvent) {
    throw new Error('Браузер пока не предлагает установку. Откройте приложение по HTTPS и попробуйте после полной загрузки страницы.')
  }

  const promptEvent = installPromptEvent
  installPromptEvent = null
  notifyInstallStateListeners()

  await promptEvent.prompt()
  const choice = await promptEvent.userChoice
  notifyInstallStateListeners()

  return choice
}

export async function refreshApplication() {
  await clearAppCaches()

  if (!('serviceWorker' in navigator)) {
    window.location.reload()
    return
  }

  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) {
    window.location.reload()
    return
  }

  await registration.update()
  const waitingWorker = registration.waiting || registration.installing

  if (waitingWorker) {
    const reloadReady = reloadWhenControllerChanges()
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })
    await reloadReady
  }

  window.location.reload()
}
